import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";

import {
  Layout,
  Link,
  Icon,
  Button,
  ProgressBar,
  Text,
  BlockStack,
  InlineStack,
  Truncate,
  Toast,
  Popover,
  ActionList,
} from "@shopify/polaris";
import { ChevronRightIcon, ShareIcon } from "@shopify/polaris-icons";

// import ShareModal from "../../components/ShareModal";
import ShareModal from "../../../../../components/Opportunity/ShareModal";
import QRCodeModal from "../../../../../components/Opportunity/QRCodeModal";

const ShareHeader = (props) => {
  const history = useHistory();
  const location = useLocation();
  const {
    contactId,
    opportunity,
    vendorProgram,
    progress = 25,
  } = props;

  const navigateHome = useCallback(() => {
    history.push("/");
  }, [history]);

  const navigateSection179 = useCallback(() => {
    history.push("/section-179-calculator");
  }, [history]);

  // Branding priority: team > vendor program > static asset fallback.
  // Blank strings are nullified on save (model callback), so null means "not configured".
  const teamLogoUrl = opportunity?.attributes?.originator_team_logo_url;
  const teamHeader = opportunity?.attributes?.originator_team_public_page_header;
  const teamSubheader = opportunity?.attributes?.originator_team_public_page_subheader;

  const brandHeader = teamHeader || vendorProgram?.attributes?.public_page_header;
  const brandSubheader = teamSubheader || vendorProgram?.attributes?.public_page_subheader;

  const displayVendorLogo = () => {
    if (teamLogoUrl) {
      return <img src={teamLogoUrl} className={"left-bar-logo"} />;
    }

    if (vendorProgram.attributes.attachment_url) {
      return <img src={vendorProgram.attributes.attachment_url} className={"left-bar-logo"} />;
    }

    try {
      const vendorName = vendorProgram.attributes.name_slug;
      const src = require(`../../../../../assets/${vendorName}/logo.png`);

      if (vendorName === "default") {
        return <img src={src} className={"left-bar-logo default-logo"} />;
      }

      return <img src={src} className={"left-bar-logo"} />;
    } catch (err) {
      return;
    }
  };

  const [showContactShareModal, setShowContactShareModal] = useState(contactId != undefined || false);
  const [openQRCodeModal, setOpenQRCodeModal] = useState(false);
  const [sharePopoverActive, setSharePopoverActive] = useState(false);

  const toggleSharePopover = useCallback(
    () => setSharePopoverActive((active) => !active),
    []
  );

  // Build share actions array
  const shareActions = [
    {
      content: "Share with contacts",
      onAction: () => {
        setShowContactShareModal(true);
        setSharePopoverActive(false);
      }
    },
    // Only show QR code option if feature flag is enabled
    ...(process.env.ENABLE_QR_CODE_SHARING === 'true' ? [{
      content: "Show QR code",
      onAction: () => {
        setOpenQRCodeModal(true);
        setSharePopoverActive(false);
      },
    }] : []),
  ];

  const [showToast, setShowToast] = useState(location?.state?.showToast);
  const [customToastMessage, setCustomToastMessage] = useState("");
  const toggleShowToast = useCallback(
    () => setShowToast((active) => !active),
    []
  );


  const shareModal = (
    <ShareModal
      contactId={contactId}
      opportunityId={opportunity.id}
      opportunity={opportunity}
      toggleShowToast={toggleShowToast}
      showContactShareModal={showContactShareModal}
      setCustomToastMessage={setCustomToastMessage}
      setShowContactShareModal={setShowContactShareModal}
    />
  );

  const handleDismissToast = () => {
    const { state: currentState } = location;
    if (currentState) {
      delete currentState.toastMessage;
      delete currentState.showToast;
    }

    window.history.replaceState({ ...currentState }, "");
    setCustomToastMessage("");
    toggleShowToast();
  };

  const toastMarkup = showToast ? (
    <Toast
      content={location?.state?.toastMessage || customToastMessage}
      onDismiss={handleDismissToast}
    />
  ) : undefined;

  return <>
    <Layout.Section>
      <InlineStack align="space-between">
          <InlineStack spacing="tight">
            <Link monochrome onClick={navigateHome}>
              Home
            </Link>
            <Icon source={ChevronRightIcon} tone={"subdued"} />
            <div style={{ marginTop: "5px" }}>
              <Truncate>
                <p>{opportunity.attributes.name}</p>
              </Truncate>
            </div>
          </InlineStack>

          {shareActions.length === 1 ? (
            // Single action - show as direct button
            <Button
              icon={ShareIcon}
              onClick={shareActions[0].onAction}
            >
              Share with contacts
            </Button>
          ) : (
            // Multiple actions - show as popover dropdown
            <Popover
              active={sharePopoverActive}
              activator={
                <Button
                  icon={ShareIcon}
                  onClick={toggleSharePopover}
                  disclosure="down"
                >
                  Share
                </Button>
              }
              autofocusTarget="first-node"
              onClose={toggleSharePopover}
            >
              <ActionList
                actionRole="menuitem"
                items={shareActions}
              />
            </Popover>
          )}

      </InlineStack>
    </Layout.Section>

    <Layout.Section>{displayVendorLogo()}</Layout.Section>

    {(brandHeader || brandSubheader) && (
      <Layout.Section>
        <BlockStack gap="100">
          {brandHeader && (
            <Text variant="headingMd" as="h4">{brandHeader}</Text>
          )}
          {brandSubheader && (
            <Text variant="bodySm" tone="subdued">{brandSubheader}</Text>
          )}
        </BlockStack>
      </Layout.Section>
    )}

    <Layout.Section>
      <BlockStack>
        <Text variant="headingLg" as="h5">
          {opportunity.attributes.client_name}
        </Text>

        <br/>
        
        <ProgressBar progress={progress} size="small" tone={"primary"} />
      </BlockStack>
    </Layout.Section>
    <Layout.Section></Layout.Section>

    {shareModal}
    {/* Only render QR code modal if feature flag is enabled */}
    {process.env.ENABLE_QR_CODE_SHARING === 'true' && (
      <QRCodeModal
        isOpen={openQRCodeModal}
        onClose={() => setOpenQRCodeModal(false)}
        shareUrl={`${window.location.origin}/opportunities/${opportunity.id}/share`}
        customerName={opportunity.attributes.client_name}
        customerEmail={opportunity.attributes.client_email}
        opportunityName={opportunity.attributes.name}
      />
    )}
    {toastMarkup}
  </>;
};

ShareHeader.propTypes = {
  contactId: PropTypes.string,
  opportunity: PropTypes.object,
  vendorProgram: PropTypes.object,
  progress: PropTypes.number,
};

export default ShareHeader;
